import moment from "moment";
import invariant from "ts-invariant";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Form, message } from "antd";
import { useCallback, useEffect, useState } from "react";

import sdk from "@/sdk";

export function useEditUser({
  selectedUserId,
  getUsers,
}: {
  selectedUserId?: string;
  getUsers?: () => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const editUser = useCallback(
    (values) => {
      setLoading(true);
      if (selectedUserId) {
        if (values.password) {
          if (values.password !== values.verifyPassword) {
            message.warn("Нууц үг баталгаажуулах буруу байна.");
            setLoading(false);
            return;
          }
        }

        const birthDate = new Date(values.birthDate).getTime();
        sdk
          .editUser({
            _id: selectedUserId,
            user: {
              avatar: values.avatar,
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              password: values.password,
              phoneNo: values.phoneNo,
              role: values.role,
              type: values.type,
              birthDate: Number.isNaN(birthDate) ? null : birthDate,
              bankInfo:
                values.bankName || values.accountNo || values.accountName
                  ? {
                      bankName: values.bankName,
                      accountNo: values.accountNo,
                      accountName: values.accountName,
                    }
                  : null,
            },
          })
          .then(() => {
            getUsers?.();
            message.success("Амжилттай засагдлаа", 2);
            setLoading(false);
          })
          .catch((err) => {
            message.warn(err.response?.errors[0]?.message, 2);
            setLoading(false);
          })
          .finally(() => setLoading(false));
      }
    },
    [getUsers, selectedUserId],
  );

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["user", selectedUserId],
    queryFn: () => {
      invariant(selectedUserId, "selectedUserId must be defined");
      return sdk.getUser({ _id: selectedUserId });
    },
    select: (data) => data.getUser,
    enabled: !!selectedUserId,
  });

  // Query MSCD account status if dbxAccountNumber exists
  // const { data: mscdAccountStatus, isLoading: isMscdLoading } = useQuery({
  //   queryKey: ["mscdAccountStatus", data?.dbxAccountNumber],
  //   queryFn: () => {
  //     invariant(data?.dbxAccountNumber, "dbxAccountNumber must be defined");
  //     return sdk.getMscdAccountStatus({ accountNo: data.dbxAccountNumber });
  //   },
  //   select: (data) => data.getMscdAccountStatus,
  //   enabled: !!data?.dbxAccountNumber,
  // });

  // Query user's MSCD API account
  const {
    data: userMscdAccount,
    isLoading: isLoadingMscdAccount,
    refetch: refetchMscdAccount,
  } = useQuery({
    queryKey: ["userMscdAccount", selectedUserId],
    queryFn: () => {
      invariant(selectedUserId, "selectedUserId must be defined");
      return sdk.getUserMSCDApiAccount({ userId: selectedUserId });
    },
    select: (data) => data.getUserMSCDApiAccount,
    enabled: !!selectedUserId,
  });

  // Query internal MSCDAccount (with error info)
  const {
    data: userMSCDAccountInternal,
    isLoading: isLoadingMSCDAccountInternal,
    refetch: refetchMSCDAccountInternal,
  } = useQuery({
    queryKey: ["userMSCDAccountInternal", selectedUserId],
    queryFn: () => {
      invariant(selectedUserId, "selectedUserId must be defined");
      return sdk.getUserMSCDAccount({ userId: selectedUserId });
    },
    select: (data) => data.getUserMSCDAccount,
    enabled: !!selectedUserId,
  });

  const createMSCDMutation = useMutation({
    mutationFn: (id: string) => sdk.cereateMSCDAccount({ userId: id }),
    onSuccess: () => {
      message.success("Хүсэлт амжилттай илгээлээ.", 2);
      refetchMscdAccount();
      refetchMSCDAccountInternal();
    },
    onError: (err: any) => message.warn(err.response?.errors[0]?.message, 2),
  });

  const retryMSCDMutation = useMutation({
    mutationFn: (id: string) => sdk.retryMSCDAccountCreation({ userId: id }),
    onSuccess: () => {
      message.success("Дахин илгээлээ.", 2);
      refetchMscdAccount();
      refetchMSCDAccountInternal();
    },
    onError: (err: any) => message.warn(err.response?.errors[0]?.message, 2),
  });

  const fixAccountNoMutation = useMutation({
    mutationFn: (id: string) => sdk.fixMSCDAccountNo({ userId: id }),
    onSuccess: (data) => {
      message.success(`BDCAccountId засагдлаа: ${data.fixMSCDAccountNo}`, 3);
      refetchMscdAccount();
      refetchMSCDAccountInternal();
    },
    onError: (err: any) => message.warn(err.response?.errors[0]?.message, 2),
  });

  const updateAccountNoMutation = useMutation({
    mutationFn: async (params: {
      userId: string;
      accountNo: string;
      retry?: boolean;
    }) => {
      await sdk.updateMSCDAccountNo({
        userId: params.userId,
        accountNo: params.accountNo,
      });
      if (params.retry) {
        await sdk.retryMSCDAccountCreation({ userId: params.userId });
      }
    },
    onSuccess: (_, variables) => {
      message.success(
        variables.retry
          ? "Дансны дугаар засагдаж, дахин илгээгдлээ"
          : "Дансны дугаар засагдлаа",
        2,
      );
      refetchMscdAccount();
      refetchMSCDAccountInternal();
    },
    onError: (err: any) => message.warn(err.response?.errors?.[0]?.message, 2),
  });

  const updateBankInfoMutation = useMutation({
    mutationFn: (params: {
      _id: string;
      bankInfo: {
        bankName: string;
        bankCode?: string;
        accountNo: string;
        accountName?: string;
      };
    }) => sdk.updateUserBankInfo(params),
    onSuccess: () => {
      message.success("Дансны мэдээлэл амжилттай шинэчлэгдлээ.", 2);
      refetch();
    },
    onError: (err: any) => message.warn(err.response?.errors[0]?.message, 2),
  });
  useEffect(() => {
    if (error) {
      message.warn((error as unknown as Error).message);
    }
  }, [error]);
  useEffect(() => {
    if (!data) {
      return;
    }
    form.setFields([
      {
        name: "lastName",
        value: data.lastName,
      },
      {
        name: "firstName",
        value: data.firstName,
      },
      {
        name: "email",
        value: data.email,
      },
      {
        name: "phoneNo",
        value: data.phoneNo,
      },
      {
        name: "role",
        value: data.role,
      },
      {
        name: "type",
        value: data.type,
      },
      {
        name: "birthDate",
        value: data.birthDate ? moment(new Date(data?.birthDate)) : "",
      },
      {
        name: "kycInfo",
        value: data.kycInfo.status,
      },
      {
        name: "bankName",
        value: data.bankInfo?.bankName,
      },
      {
        name: "accountNo",
        value: data.bankInfo?.accountNo,
      },
      {
        name: "accountName",
        value: data.bankInfo?.accountName,
      },
    ]);
  }, [data, form]);

  const addUser = useCallback(
    (values) => {
      setLoading(true);
      if (values.password) {
        if (values.password !== values.verifyPassword) {
          message.warn("Нууц үг баталгаажуулах буруу байна.");
          setLoading(false);
          return;
        }
      }
      delete values.verifyPassword;

      const birthDate = new Date(values.birthDate).getTime();
      sdk
        .addUser({
          user: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNo: values.phoneNo,
            role: values.role,
            type: values.type,
            birthDate: Number.isNaN(birthDate) ? null : birthDate,
            bankInfo: values.bankName &&
              values.accountNo &&
              values.accountName && {
                bankName: values.bankName,
                accountNo: values.accountNo,
              },
            password: values.password,
          },
        })
        .then(() => {
          getUsers?.();
          message.success("Амжилттай нэмэгдлээ.", 2);
          form.resetFields();
          setLoading(false);
        })
        .catch((err) => {
          message.warn(err.response?.errors[0]?.message, 2);
          setLoading(false);
        })
        .finally(() => setLoading(false));
    },
    [form, getUsers],
  );
  return {
    addUser,
    editUser,
    form,
    loading: loading ?? isLoading,
    userData: data,
    createMSCD: createMSCDMutation.mutate,
    isCreatingMSCD: createMSCDMutation.isLoading,
    // mscdAccountStatus,
    userMscdAccount,
    isLoadingMscdAccount,
    updateBankInfo: updateBankInfoMutation.mutate,
    isUpdatingBankInfo: updateBankInfoMutation.isLoading,
    userMSCDAccountInternal,
    isLoadingMSCDAccountInternal,
    retryMSCD: retryMSCDMutation.mutate,
    isRetryingMSCD: retryMSCDMutation.isLoading,
    fixAccountNo: fixAccountNoMutation.mutate,
    isFixingAccountNo: fixAccountNoMutation.isLoading,
    updateAccountNo: updateAccountNoMutation.mutate,
    isUpdatingAccountNo: updateAccountNoMutation.isLoading,
  };
}
